import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../interceptors/error.interceptor';
import { extractAdminSettings } from '../utils/admin-governance.util';

export const MAINTENANCE_STORAGE_KEY = 'karibuu.maintenance.active';

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/v1`;

  private readonly activeSignal = signal(this.readLocalFlag());
  private lastCheckedAt = 0;
  private readonly cacheMs = 15_000;

  readonly isActive = this.activeSignal.asReadonly();

  /** Apply a known status (e.g. after admin save). */
  setActive(active: boolean): void {
    this.activeSignal.set(active);
    this.writeLocalFlag(active);
    this.lastCheckedAt = Date.now();
  }

  markFromHttpError(error: { status?: number; error?: unknown } | null | undefined): boolean {
    if (!error) return false;
    if (error.status === 503 || this.hasMaintenanceCode(error.error)) {
      this.setActive(true);
      return true;
    }
    return false;
  }

  /**
   * Refresh maintenance status for client/partner areas via GET /maintenance/status.
   * Falls back to the last local flag if the endpoint is unreachable.
   */
  ensureChecked(force = false): Observable<boolean> {
    const now = Date.now();
    if (!force && now - this.lastCheckedAt < this.cacheMs) {
      return of(this.activeSignal());
    }

    return this.fetchMaintenanceStatus().pipe(
      // Never block the public shell forever if the status endpoint hangs (CI / flaky network).
      timeout(5_000),
      catchError(() => of(this.readLocalFlag())),
      map((active) => {
        this.setActive(active);
        return active;
      }),
    );
  }

  private fetchMaintenanceStatus(): Observable<boolean> {
    const context = new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true);
    return this.http
      .get<unknown>(`${this.apiUrl}/maintenance/status`, { context })
      .pipe(map((response) => this.parseMaintenanceFlag(response)));
  }

  parseMaintenanceFlag(response: unknown): boolean {
    if (!response || typeof response !== 'object') {
      return false;
    }

    const root = response as Record<string, unknown>;
    const data = root['data'];

    if (typeof data === 'boolean') {
      return data;
    }

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const record = data as Record<string, unknown>;
      const direct =
        record['maintenance_mode'] ??
        record['maintenanceMode'] ??
        record['app.maintenance_mode'] ??
        (record['settings'] && typeof record['settings'] === 'object'
          ? (record['settings'] as Record<string, unknown>)['app.maintenance_mode'] ??
            (record['settings'] as Record<string, unknown>)['maintenance_mode']
          : undefined);
      if (direct !== undefined) {
        return this.isTruthy(direct);
      }
    }

    try {
      const settings = extractAdminSettings(response);
      const maintenance = settings.find((item) => item.key === 'app.maintenance_mode');
      if (maintenance) {
        return this.isTruthy(maintenance.value);
      }
    } catch {
      /* ignore */
    }

    return false;
  }

  isTruthy(value: unknown): boolean {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }

  private hasMaintenanceCode(payload: unknown): boolean {
    if (!payload || typeof payload !== 'object') return false;
    const record = payload as Record<string, unknown>;
    const error = record['error'];
    const code =
      (typeof error === 'object' && error
        ? String((error as Record<string, unknown>)['code'] || '')
        : '') || String(record['code'] || '');
    const message = String(record['message'] || '').toLowerCase();
    return (
      code.toUpperCase().includes('MAINTENANCE') ||
      message.includes('maintenance')
    );
  }

  private readLocalFlag(): boolean {
    try {
      return localStorage.getItem(MAINTENANCE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private writeLocalFlag(active: boolean): void {
    try {
      if (active) {
        localStorage.setItem(MAINTENANCE_STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }
}
